//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from './service';
import StatesModel from '../models/states.model';

/**
 * @service StatesAPIService
 * @description Service for managing geographic state and village information within the Trading Community module.
 * This service provides functionality to retrieve lists of states and their corresponding villages.
 * It is primarily used to populate dropdown menus in address forms (e.g., User Registration, Farm Site creation).
 *
 * The service interacts with two main endpoints:
 * 1. `/tradingcommunity/stateinfo/`: For state-level information (inherited from base Service).
 * 2. `/tradingcommunity/compstream/villages/`: For retrieving villages based on a selected state.
 *
 * **Key Responsibilities:**
 * 1. **State Retrieval**: Fetching the master list of states available in the system.
 * 2. **Village Retrieval**: Fetching villages dynamically based on the user's state selection.
 * 3. **Resource Management**: Handling HTTP request lifecycles to prevent memory leaks.
 *
 * **Data Flow:**
 * - **States**: Component calls inherited `getAll()` (implied) -> Service calls `/tradingcommunity/stateinfo/`.
 * - **Villages**: Component calls `getVillagesByStates('StateName')` -> Service calls `/tradingcommunity/compstream/villages/?state=StateName`.
 *
 * @feature 1. Retrieve master list of states.
 * @feature 2. Filter villages by state name.
 * @feature 3. Robust management of concurrent HTTP requests.
 *
 * @architecture
 * Extends base Service<StatesModel> providing REST API integration.
 * Uses HttpClient for HTTP operations and manages subscriptions for cleanup.
 *
 * @service-endpoint /tradingcommunity/stateinfo/
 * @service-provider root (singleton, application-wide)
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class StatesAPIService extends Service<StatesModel> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions.
   * Used to maintain references to active Observables so they can be unsubscribed
   * when the service is destroyed or when cancellation is requested.
   * @default []
   * @side-effect Used to store subscription references for cancellation via cancelOnGoingHttpRequests()
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient.
   *
   * Initializes the service by calling the parent Service constructor with the
   * HttpClient instance and the specific API endpoint path (`/tradingcommunity/stateinfo/`).
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests to the backend.
   * @returns {void}
   */
  constructor(httpClient: HttpClient) {
    super(httpClient, '/tradingcommunity/stateinfo/');
  }

  /**
   * @method getVillagesByStates
   * @async
   * @description Fetch a list of villages belonging to a specific state.
   *
   * This method hits a specific endpoint (`/tradingcommunity/compstream/villages/`) rather than the base service URL.
   * It is used to cascade selection in UI forms (Select State -> Load Villages).
   *
   * @param {String} selectedState - The name or code of the state to filter villages by.
   * @returns {Promise<Array<any>>} Promise resolving to an array of village objects (or strings, depending on API).
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP GET request.
   * - Pushes the subscription to `ongoingRequests`.
   *
   * @example
   * statesAPIService.getVillagesByStates('Andhra Pradesh').then(villages => {
   *   console.log('Villages:', villages);
   * });
   */
  public getVillagesByStates(selectedState: String) {
    return new Promise<Array<any>>((resolve, reject) => {
      const request = this.httpClient.get<Array<any>>(`${this.baseUrl}/tradingcommunity/compstream/villages/?state=${selectedState}`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  //To Cancel the onGoing Http Requests
  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks.
   *
   * This method iterates through the `ongoingRequests` array and calls `unsubscribe()`
   * on each subscription. This is typically called when a component using this service
   * is destroyed (ngOnDestroy) to ensure that no network requests continue in the background.
   *
   * @returns {void}
   * @side-effect
   * - Aborts all pending HTTP requests tracked in `ongoingRequests`.
   * - Resets `ongoingRequests` to an empty array.
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
